import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { useViewContext } from "@/contexts/ViewContext";
import { lockedWorkspace } from "@/lib/workspaceMode";

function onCleanerRoute(path: string): boolean {
  return path.startsWith("/worker") || path.startsWith("/cleaner") || path.startsWith("/clean/");
}

function onPropertyWorkspace(path: string): boolean {
  return path.startsWith("/owner") || path.startsWith("/properties");
}

/**
 * When an owner switches a cleaner to Team, move her onto Properties.
 * When they switch her back, move her onto the cleaner schedule.
 */
export function WorkspaceRoleSync() {
  const { is_owner, is_cohost, is_cleaner, has_cleaner_assignment, isLoading } = useUserCapabilities();
  const { setViewContext } = useViewContext();
  const navigate = useNavigate();
  const location = useLocation();
  const prevCohost = useRef<boolean | null>(null);

  useEffect(() => {
    if (isLoading) return;
    const locked = lockedWorkspace({ is_owner, is_cohost, is_cleaner, has_cleaner_assignment });
    const wasCohost = prevCohost.current;
    prevCohost.current = is_cohost;
    const path = location.pathname;

    if (locked === "owner") {
      setViewContext("owner");
      const justBecameTeam = wasCohost === false && is_cohost;
      if (onCleanerRoute(path) || (justBecameTeam && !onPropertyWorkspace(path))) {
        navigate("/owner/properties", { replace: true });
      }
      return;
    }

    if (locked === "cleaner") {
      setViewContext("cleaner");
      const justLostTeam = wasCohost === true && !is_cohost;
      if (justLostTeam && onPropertyWorkspace(path)) {
        navigate("/worker/schedule", { replace: true });
      }
    }
  }, [isLoading, is_owner, is_cohost, is_cleaner, has_cleaner_assignment, location.pathname, navigate, setViewContext]);

  return null;
}
